/**
 * Class strings shared by several ABM components — abmparking.com's layout grid, type scale and
 * pill buttons. The font stays Roboto (the portal font); every size, weight, colour and spacing
 * mirrors abmparking.com's own stylesheet.
 *
 * Sizes the token scale lacks (18 / 21 / 42 / 64px and the source's fluid `clamp()` headings) are
 * arbitrary values here, in one place, rather than new global tokens: they belong to this tenant
 * only. The `length:` hint tells Tailwind (and tailwind-merge) a `clamp()` is a font size.
 */

/** abmparking.com's `.container`: 90% wide, capped at 1280px. */
export const abmContainer = 'tw:mx-auto tw:w-[90%] tw:max-w-[80rem]';

/** A page section's vertical rhythm (`.abm2-section`, 96px). */
export const abmSectionY = 'tw:py-24';

/** A section that directly follows another white section drops its top padding, as on the source. */
export const abmSectionContinued = 'tw:pt-0';

/** Soft navy shadow under white text on photos. */
export const abmTextShadow =
  'tw:[text-shadow:0.05em_0.05em_0.1em_color-mix(in_srgb,var(--tw-color-abm-navy)_50%,transparent)]';

/** Banner h1 — 64px / 500 on desktop, 36px on phones. */
export const abmHeroTitle =
  'tw:text-[length:clamp(2.25rem,0.5rem+5.8333vw,4rem)] tw:leading-[1.1] tw:font-medium tw:text-pretty tw:text-white';

/** 18px lead copy (banner paragraph, city list). */
export const abmLeadText = 'tw:text-[1.125rem] tw:leading-3';

/** Centered / band section title — 48px / 600. */
export const abmSectionTitle =
  'tw:text-[length:clamp(1.75rem,0.5rem+4.1667vw,3rem)] tw:leading-[1.1] tw:font-semibold';

/** Content-row title — 42px / 600. */
export const abmRowTitle =
  'tw:text-[length:clamp(1.75rem,0.875rem+2.9167vw,2.625rem)] tw:leading-[1.2] tw:font-semibold tw:text-pretty';

/** Body copy — 16px / 1.5, ink. */
export const abmBodyText = 'tw:text-16 tw:leading-3 tw:text-abm-ink';

/** In-copy subheading (`.wysiwyg h3`) — 21px / 600. */
export const abmSubheading =
  'tw:text-[length:clamp(1.125rem,0.9596rem+0.5515vw,1.3125rem)] tw:leading-[1.4] tw:font-semibold tw:text-pretty';

/** Bulleted list with the source's small blue dot markers. */
export const abmBulletList = 'tw:flex tw:flex-col tw:gap-base';

export const abmBulletItem =
  "tw:relative tw:pl-[calc(1em+0.25rem)] tw:before:absolute tw:before:left-0 tw:before:top-[0.5em] tw:before:size-[0.5em] tw:before:rounded-full tw:before:bg-abm-blue tw:before:content-['']";

/** Blue pill button (`.abm2-button`). */
export const abmPillButton =
  'tw:inline-flex tw:h-11 tw:items-center tw:justify-center tw:whitespace-nowrap tw:rounded-full tw:bg-abm-blue tw:px-massive tw:text-16 tw:font-bold tw:text-white tw:no-underline tw:transition-colors tw:hover:bg-abm-blue-hover';

/** Orange variant — navy text (`.abm2-button-orange`). */
export const abmPillButtonOrange = 'tw:bg-abm-orange tw:text-abm-navy tw:hover:bg-abm-orange-hover';

/** Large variant (`.abm2-button-large`, 64px tall / 21px from 640px). */
export const abmPillButtonLarge = 'tw:sm:h-16 tw:sm:text-[1.3125rem]';

/** Underlined text link offset like the source's (`text-underline-offset: .25em`). */
export const abmUnderline = 'tw:underline tw:underline-offset-[0.25em]';
